import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import BookingsView from '../pages/BookingsView';
import MessagesView from '../pages/MessagesView';
import DocumentsView from '../pages/DocumentsView';
import BillingView from '../pages/BillingView';
import { motion } from 'framer-motion';
import { FiCalendar, FiMessageSquare, FiFileText, FiCreditCard, FiGrid, FiLogOut, FiUser, FiEdit, FiChevronRight, FiMail, FiClock } from 'react-icons/fi';

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');

  const handleLogout = () => {
    onLogout();
    navigate('/login');
  };

  const userId = user?.id;
  const isLawyer = user?.user_type === 'lawyer';
  const isClient = user?.user_type === 'client';

  const tabs = [
    { key: 'overview', label: 'Overview', icon: <FiGrid className="w-4 h-4" /> },
    { key: 'bookings', label: 'Bookings', icon: <FiCalendar className="w-4 h-4" /> },
    { key: 'messages', label: 'Messages', icon: <FiMessageSquare className="w-4 h-4" /> },
    { key: 'documents', label: 'Documents', icon: <FiFileText className="w-4 h-4" /> },
    { key: 'billing', label: 'Billing', icon: <FiCreditCard className="w-4 h-4" /> },
  ];

  const quickCards = [
    { key: 'bookings', icon: <FiCalendar className="w-6 h-6" />, title: 'My Bookings', desc: 'View your scheduled sessions', color: 'from-primary-500 to-primary-600' },
    { key: 'messages', icon: <FiMessageSquare className="w-6 h-6" />, title: 'Messages', desc: 'Chat with your lawyer', color: 'from-teal-500 to-teal-600' },
    { key: 'documents', icon: <FiFileText className="w-6 h-6" />, title: 'Documents', desc: 'Access your case files', color: 'from-accent-500 to-accent-600' },
    { key: 'billing', icon: <FiCreditCard className="w-6 h-6" />, title: 'Billing', desc: 'View invoices & payments', color: 'from-primary-400 to-primary-500' },
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Header */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0f1f35 0%, #1e3a5f 50%, #172e4d 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-5 right-10 w-64 h-64 rounded-full" style={{ background: 'radial-gradient(circle, rgba(212,175,55,0.4) 0%, transparent 70%)' }} />
        </div>
        <div className="max-w-6xl mx-auto px-4 py-10 relative">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <motion.h1 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-2xl md:text-3xl font-bold text-white mb-1">Welcome, {user?.full_name}!</motion.h1>
              <p className="text-white/60 text-sm">Manage your account and bookings</p>
            </div>
            <button onClick={handleLogout} className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 border border-white/10 text-white/70 hover:bg-white/20 transition-colors text-sm">
              <FiLogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="max-w-6xl mx-auto px-4 -mt-5 relative z-10">
        <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-1.5 flex overflow-x-auto scrollbar-hide">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${activeTab === tab.key ? 'bg-primary-500 text-white shadow-md' : 'text-secondary-500 hover:bg-secondary-50'}`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {activeTab === 'overview' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            {/* Lawyer Profile Setup CTA */}
            {isLawyer && (
              <Link to="/lawyer-profile-setup" className="block mb-6">
                <div className="bg-gradient-to-r from-accent-500 to-accent-600 rounded-2xl p-5 flex items-center justify-between hover:shadow-lg transition-shadow">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center"><FiEdit className="w-5 h-5 text-white" /></div>
                    <div><h3 className="font-semibold text-white">Complete Lawyer Profile</h3><p className="text-white/60 text-sm">Set up your professional profile to appear in our directory</p></div>
                  </div>
                  <FiChevronRight className="w-5 h-5 text-white/60" />
                </div>
              </Link>
            )}

            {/* Quick Actions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              {quickCards.map(card => (
                <div key={card.key} onClick={() => setActiveTab(card.key)} className="bg-white rounded-2xl shadow-card border border-secondary-100 p-5 cursor-pointer hover:shadow-card-hover transition-all group">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center text-white mb-3 group-hover:scale-105 transition-transform`}>{card.icon}</div>
                  <h3 className="font-semibold text-secondary-900 mb-1">{card.title}</h3>
                  <p className="text-xs text-secondary-500">{card.desc}</p>
                </div>
              ))}
            </div>

            {/* Profile Info */}
            <div className="bg-white rounded-2xl shadow-card border border-secondary-100 p-6">
              <h2 className="text-lg font-semibold text-secondary-900 mb-5">Profile Information</h2>
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="text-center sm:text-left">
                  <div className="w-20 h-20 rounded-2xl overflow-hidden bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center mx-auto sm:mx-0">
                    {user?.profile_image ? (
                      <img src={`${process.env.REACT_APP_API_URL}${user.profile_image}`} alt={user?.full_name} className="w-full h-full object-cover" />
                    ) : (
                      <FiUser className="w-8 h-8 text-primary-400" />
                    )}
                  </div>
                  {isLawyer && (
                    <Link to="/lawyer-profile-setup" className="text-xs text-primary-500 hover:underline mt-2 inline-block">Update Picture</Link>
                  )}
                </div>
                <div className="flex-1 space-y-3">
                  {[
                    { icon: <FiUser className="w-4 h-4" />, label: 'Full Name', value: user?.full_name },
                    { icon: <FiMail className="w-4 h-4" />, label: 'Email', value: user?.email },
                    { icon: <FiGrid className="w-4 h-4" />, label: 'Account Type', value: isLawyer ? 'Lawyer' : 'Client' },
                    { icon: <FiClock className="w-4 h-4" />, label: 'Member Since', value: new Date(user?.created_at).toLocaleDateString() },
                  ].map((info, i) => (
                    <div key={i} className="flex items-center gap-3 py-2 border-b border-secondary-50 last:border-0">
                      <span className="text-secondary-400">{info.icon}</span>
                      <span className="text-sm text-secondary-500 w-28">{info.label}</span>
                      <span className="text-sm font-medium text-secondary-900">{info.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'bookings' && userId && <BookingsView userId={userId} />}
        {activeTab === 'messages' && userId && <MessagesView userId={userId} />}
        {activeTab === 'documents' && userId && <DocumentsView userId={userId} />}
        {activeTab === 'billing' && userId && <BillingView userId={userId} />}
      </div>
    </div>
  );
}

export default Dashboard;
